import {
  JwtPayloadSchema,
  type UserProfile,
  type Result,
  type SessionError,
} from '@/shared/schemas/auth';

const SESSION_COOKIE_NAME = '__session';

/**
 * Extracts the session cookie value.
 */
function getSessionCookie(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === SESSION_COOKIE_NAME && value) {
      return value;
    }
  }
  return null;
}

/**
 * Decodes and validates a JWT payload using Zod.
 * Only decodes the payload - does NOT verify the signature.
 * Signature verification requires server-side Firebase Admin SDK.
 */
function decodeJwtPayload(
  token: string
): Result<{ sub: string; email: string; name?: string; exp?: number }, string> {
  try {
    const parts = token.split('.');
    const payloadPart = parts[1];
    if (!payloadPart) {
      return { ok: false, error: 'Invalid JWT structure' };
    }

    const decoded = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
    const json: unknown = JSON.parse(decoded);

    const parsed = JwtPayloadSchema.safeParse(json);
    if (!parsed.success) {
      return { ok: false, error: parsed.error.issues[0]?.message ?? 'Invalid payload' };
    }

    return { ok: true, value: parsed.data };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to decode token';
    return { ok: false, error: message };
  }
}

/**
 * Verifies the session cookie and returns the user profile.
 * Uses Zod validation for type-safe JWT parsing.
 */
export function verifySession(): Result<UserProfile, SessionError> {
  const token = getSessionCookie();

  if (!token) {
    return { ok: false, error: { kind: 'no_cookie' } };
  }

  const payloadResult = decodeJwtPayload(token);

  if (!payloadResult.ok) {
    return { ok: false, error: { kind: 'invalid_token', message: payloadResult.error } };
  }

  const payload = payloadResult.value;

  // Check if token is expired
  if (payload.exp !== undefined && payload.exp * 1000 < Date.now()) {
    return { ok: false, error: { kind: 'expired' } };
  }

  return {
    ok: true,
    value: {
      id: payload.sub,
      email: payload.email,
      name: payload.name,
    },
  };
}
